import { useCallback } from 'react'
import { useInfiniteQuery, useQueryClient } from 'react-query'
import { notification } from 'antd'
import { searchChatHistory, getChatSessionDetail } from './chatHistoryApi'
import { ChatSearchParams, ChatSessionDetail } from '../AdminApp/Chats/chatSearchModel'

const PAGE_SIZE = 20

export const chatSessionKey = (sessionId: string) => ['chatSession', sessionId] as const

export function buildChatHistoryParams(q?: string): ChatSearchParams {
  return {
    q: q || undefined,
    page: 1,
    pageSize: PAGE_SIZE,
  }
}

export const useChatHistorySearch = (params: ChatSearchParams | null) => {
  return useInfiniteQuery(
    ['chatHistory', params],
    ({ pageParam = 1 }) => searchChatHistory({ ...params!, page: pageParam }),
    {
      enabled: !!params,
      getNextPageParam: (lastPage) => {
        const totalPages = Math.ceil(lastPage.total / lastPage.page_size)
        const nextPage = lastPage.page + 1
        return nextPage <= totalPages ? nextPage : undefined
      },
      keepPreviousData: true,
      staleTime: 30_000,
      retry: false,
      onError: () => {
        notification.error({ message: 'Failed to load chat history' })
      },
    }
  )
}

export const useFetchChatSession = () => {
  const queryClient = useQueryClient()
  return useCallback(
    (sessionId: string): Promise<ChatSessionDetail> =>
      queryClient.fetchQuery({
        queryKey: chatSessionKey(sessionId),
        queryFn: () => getChatSessionDetail(sessionId),
        staleTime: Infinity,
      }),
    [queryClient]
  )
}

export const useInvalidateChatHistory = () => {
  const queryClient = useQueryClient()
  return useCallback(
    () => queryClient.invalidateQueries(['chatHistory']),
    [queryClient]
  )
}
